import { useCallback, useMemo } from 'react';

import useRole from './useRole';

type TeamId = number | string | null | undefined;

const useAccessibleTeamIds = () => {
  const { isProgramAssistant, role } = useRole();

  const accessibleTeamIds = useMemo(() => role?.teams?.map(team => team.id) ?? [], [role?.teams]);

  const isTeamAccessible = useCallback(
    (teamId: TeamId): boolean => {
      const numericTeamId = Number(teamId);

      return !isProgramAssistant || (!!numericTeamId && accessibleTeamIds.includes(numericTeamId));
    },
    [accessibleTeamIds, isProgramAssistant],
  );

  const getAccessibleTeamId = useCallback(
    (...teamIds: TeamId[]): number | undefined => {
      const numericTeamIds = teamIds.map(Number).filter(Boolean);

      if (!isProgramAssistant) {
        return numericTeamIds[0];
      }

      return (
        numericTeamIds.find(teamId => accessibleTeamIds.includes(teamId)) ?? accessibleTeamIds[0]
      );
    },
    [accessibleTeamIds, isProgramAssistant],
  );

  const getAccessibleTeamIdString = useCallback(
    (...teamIds: TeamId[]): string | undefined => {
      const teamId = getAccessibleTeamId(...teamIds);

      return teamId ? String(teamId) : undefined;
    },
    [getAccessibleTeamId],
  );

  return {
    accessibleTeamIds,
    getAccessibleTeamId,
    getAccessibleTeamIdString,
    isTeamAccessible,
  };
};

export default useAccessibleTeamIds;
